import { CloseOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Heading, SearchInput, Spinner } from '@mm/components';
import { useDebounce } from '@mm/hooks';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useCampaignsOverviewCRUDActions } from '@mm/state/actions/CampaignsOverviewCRUDActions';
import { CampaignOverviewItemType } from '@mm/state/model/CampaignOverviewModel';
import {
  CampaignAudienceAttribute,
  CampaignType,
} from '@mm/state/model/CampaignsModel';
import { IAddAudienceToAutomationModalProps } from '@mm/state/modules/modalsState/props';
import { selectRecordsByUiFilterKind } from '@mm/state/selectors/campaignsOverviewSelectors';
import { selectAudienceSorted } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { getUniqId } from '@mm/utils';
import { notification } from 'antd';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Waypoint } from 'react-waypoint';
import { AudienceNameEditBar } from '../../../editor/features/beta/modules/Audiences/AudienceNameEditBar';
import { useAutomations } from '../../../editor/features/beta/modules/Automations/use-automations';
import { LeadsCreationPendingInfoAlert } from '../../../editor/features/beta/modules/GetLeads/LeadsSearch/PurchaseContactsTable/LeadsCreationPendingInfoAlert/LeadsCreationPendingInfoAlert';
import { BotBuilderRoutes } from '../../../routes/bot-builder-routes';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import { OnClickElsewhere } from '../../components/OnClickElsewhere/OnClickElsewhere';
import styles from './AddAudienceToAutomationModal.module.css';

const PER_PAGE = 25;

export const AddAudienceToAutomationModal = (
  props: IAddAudienceToAutomationModalProps,
) => {
  const {
    closeModal,
    activeAudience,
    usePurchaseLeadsLayout,
    onAfterClose,
    withAlert,
  } = props;

  const { campaigns: automations } =
    useSelector((state: StoreState) =>
      selectRecordsByUiFilterKind(state, 'automation'),
    ) || {};
  const botId = useSelector(activeChatbotIdSelector);
  const isBusy = useSelector(
    (state: StoreState) => state.campaignsOverview.busy,
  );
  const pagination = useSelector(
    (state: StoreState) =>
      state.campaignsOverview.records.automation?.pagination,
  );
  const hasMore = !pagination?.isLastPage;
  const nextPage = Number(pagination?.page) + 1 || 1;

  const audience = useSelector(selectAudienceSorted).find(
    a => a.id === activeAudience?.id,
  );

  const { fetchAutomations, createAutomation } = useAutomations();
  const { updateCampaign } = useCampaignCRUDActions();
  const { invalidateAll } = useCampaignsOverviewCRUDActions();
  const history = useHistory();

  const [selectedAutomation, setSelectedAutomation] = useState<
    CampaignOverviewItemType | undefined
  >(undefined);
  const [nameEditMode, setNameEditMode] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    invalidateAll();
    fetchAutomations({
      page: 1,
      perPage: PER_PAGE,
      search,
      mergeResults: true,
    });

    return () => {
      invalidateAll();
    };
  }, [debouncedSearch]);

  const onClose = async () => {
    closeModal?.();
    await onAfterClose?.();
  };

  const openNotification = () => {
    notification.success({
      message: `Audience ${activeAudience?.name} successfully added`,
    });
  };

  const onAddToAutomation = async () => {
    const hash = btoa(getUniqId().toString());
    const existingGroupName =
      selectedAutomation?.campaignAudiencesAttributes?.find(
        a => a.groupName,
      )?.groupName;

    if (selectedAutomation && activeAudience) {
      await updateCampaign(
        {
          id: selectedAutomation.id,
          campaignAudiencesAttributes: [
            {
              audienceId: activeAudience?.id,
              kind: 'included',
              groupName: existingGroupName
                ? existingGroupName
                : `${selectedAutomation.id}_${hash}`,
            },
          ],
        } as CampaignType,
        botId,
      );
    }
    closeModal?.();
    openNotification();
  };

  const onAddAndGoToAutomation = () => {
    onAddToAutomation();
    if (selectedAutomation) {
      history.push(
        BotBuilderRoutes.automationEdit.getLink({
          chatbotId: botId,
          id: selectedAutomation.id,
        }),
      );
    }
  };

  const onCreateAutomation = async () => {
    const campaignAudiencesAttributes =
      activeAudience &&
      ([
        {
          audienceId: activeAudience?.id,
          kind: 'included',
          groupName: btoa(getUniqId().toString()),
        },
      ] as CampaignAudienceAttribute[]);
    await createAutomation(
      'email_sold_automation',
      undefined,
      undefined,
      campaignAudiencesAttributes,
    );
    closeModal?.();
    openNotification();
  };

  const onMoreRequest = async () => {
    setFetchingMore(true);
    await fetchAutomations({
      page: nextPage,
      perPage: PER_PAGE,
      search,
      mergeResults: true,
    });
    setFetchingMore(false);
  };

  if (!activeAudience) {
    return <></>;
  }

  return (
    <OnClickElsewhere
      handleClick={() => (!usePurchaseLeadsLayout ? onClose() : {})}
    >
      <div className={styles.container}>
        <CloseOutlined className={styles.closeIcon} onClick={onClose} />
        <div>
          <Heading size={2} className={styles.heading}>
            Add Audience to Automations
          </Heading>
          <div className={styles.audienceContainer}>
            <div className={styles.audience}>
              {nameEditMode ? (
                <AudienceNameEditBar
                  activeAudience={audience || activeAudience}
                  onEndEditing={() => setNameEditMode(false)}
                  className={styles.audienceEditBar}
                  css={{ input: styles.audienceEditBarInput }}
                  debounceTimeout={1000}
                />
              ) : (
                <>
                  <span>{audience?.name || activeAudience.name}</span>
                  <EditOutlined
                    onClick={() => setNameEditMode(true)}
                    className={styles.editIcon}
                  />
                </>
              )}
            </div>
            {!nameEditMode && !usePurchaseLeadsLayout && (
              <span className={styles.contactsCount}>
                {activeAudience.contactsCount} contacts
              </span>
            )}
          </div>
        </div>

        {withAlert && (
          <LeadsCreationPendingInfoAlert
            title="Your new leads will be added to your automation."
            description="This process may take up to 15 minutes."
          />
        )}

        <div className={styles.content}>
          <div className={styles.titleContainer}>
            <Heading size={3} className={styles.heading}>
              Add to existing Automations
            </Heading>
            <div className={styles.subheading}>
              Reach your leads out via Email or SMS. Build your own marketing
              flow, also known as Automation.
            </div>
            <div className={styles.subheading}>
              Set up time and conditions of who and when will receive your
              messages.
            </div>
          </div>
          <SearchInput
            placeholder="Search"
            css={{ container: styles.searchInput }}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />

          <div className={styles.automationsList}>
            {(!isBusy || (isBusy && fetchingMore)) &&
              automations?.map(automation => {
                return (
                  <div
                    key={automation.id}
                    className={classNames(styles.automation, styles.heading, {
                      [styles.automationActive]:
                        selectedAutomation?.id === automation.id,
                    })}
                    onClick={() => setSelectedAutomation(automation)}
                  >
                    {automation.name}
                  </div>
                );
              })}
            {!isBusy && !automations?.length && (
              <div className={styles.empty}>
                No automations found
                <PrimaryButton
                  className={styles.secondaryButton}
                  onClick={onCreateAutomation}
                >
                  <PlusOutlined />
                  Create new Automation
                </PrimaryButton>
              </div>
            )}
            {hasMore && !isBusy && (
              <Waypoint bottomOffset="-20px" onEnter={onMoreRequest} />
            )}
            {isBusy && (
              <div className={styles.spinner}>
                {' '}
                <Spinner
                  size={16}
                  standalone
                  position="absolute"
                  width="auto"
                />{' '}
                Loading automations...{' '}
              </div>
            )}
          </div>
        </div>
        {!usePurchaseLeadsLayout && (
          <>
            <PrimaryButton
              className={styles.secondaryButton}
              onClick={onCreateAutomation}
            >
              <PlusOutlined />
              Create new Automation
            </PrimaryButton>
            <div className={styles.footer}>
              <PrimaryButton
                className={styles.secondaryButton}
                disabled={!selectedAutomation}
                onClick={onAddToAutomation}
              >
                Add to Automation
              </PrimaryButton>
              <PrimaryButton
                className={styles.primaryButton}
                disabled={!selectedAutomation}
                onClick={onAddAndGoToAutomation}
              >
                Add & Go to Automation
              </PrimaryButton>
            </div>
          </>
        )}
        {usePurchaseLeadsLayout && (
          <div className={classNames(styles.footer, styles.spaceBetween)}>
            <PrimaryButton
              className={styles.primaryButton}
              disabled={!selectedAutomation}
              onClick={onAddAndGoToAutomation}
            >
              Add to Automation
            </PrimaryButton>

            <PrimaryButton
              className={styles.secondaryButton}
              onClick={onCreateAutomation}
            >
              <PlusOutlined />
              Create new Automation
            </PrimaryButton>
          </div>
        )}
      </div>
    </OnClickElsewhere>
  );
};
